import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { fundingRateIsCurrent } from '../amount/fx.js';
import { useFundingProfileQuote } from '../customer/funding-profile-react.js';
import { fundingSettlement } from '../discovery/settlement-asset.js';
import { FundingCurrencyError, unavailableFundingFx } from '../operations/currency-diagnostics.js';
import { providerFailure } from '../provider-error.js';
import { fetchFundingQuote } from './fetch-funding-quote.js';
export function useFundingQuotePreview(provider, intent, enabled, contextKey = '', diagnose, purpose = intent?.method === 'card' && !intent.providerPaymentMethodId ? 'indicative' : 'execution') {
  const [bounds, setBounds] = useState(() => new Map());
  const scope = intent ? JSON.stringify([contextKey, purpose, intent.destination, intent.receiveTarget?.fx.source, intent.receiveTarget?.fx.observedAt, intent.fiatCurrency, intent.fiatDecimals, intent.method, intent.providerPaymentMethodId, intent.settlement.provider_asset_code]) : '';
  const cachedBound = bounds.get(scope);
  const minimum = cachedBound && cachedBound.expiresAt > Date.now() ? cachedBound.failure : undefined;
  useEffect(() => {
    if (!cachedBound) return;
    const timer = setTimeout(() => setBounds(previous => {
      const next = new Map(previous);
      next.delete(scope);
      return next;
    }), Math.max(1, cachedBound.expiresAt - Date.now()));
    return () => clearTimeout(timer);
  }, [cachedBound, scope]);
  const belowMinimum = Boolean(intent && minimum?.amountBoundary && BigInt(intent.principalUnits) < BigInt(minimum.amountBoundary.amountUnits));
  const noticeKey = enabled && belowMinimum && intent ? JSON.stringify([scope, intent.principalUnits, minimum?.amountBoundary?.amountUnits]) : null;
  const [settledNotice, setSettledNotice] = useState(null);
  useEffect(() => {
    if (!noticeKey) {
      setSettledNotice(null);
      return;
    }
    setSettledNotice(null);
    const timer_0 = setTimeout(() => setSettledNotice(noticeKey), 800);
    return () => clearTimeout(timer_0);
  }, [noticeKey]);
  const key = enabled && intent && !belowMinimum ? JSON.stringify({
    scope,
    intent
  }) : null;
  const [revision, setRevision] = useState(0);
  const [snapshot, setSnapshot] = useState(null);
  const retry = useCallback(() => setRevision(current => current + 1), []);
  useEffect(() => {
    if (!key) {
      // Leaving amount entry (including KYC) invalidates its previous allowance evidence.
      setSnapshot(null);
      return;
    }
    const controller = new AbortController();
    const current_0 = JSON.parse(key).intent;
    setSnapshot(previous_0 => ({
      key,
      revision,
      value: previous_0?.key === key ? {
        ...previous_0.value,
        state: 'loading',
        providerFailure: null,
        error: null
      } : empty('loading', current_0.principalUnits)
    }));
    let expiry;
    const debounce = setTimeout(() => {
      void fetchFundingQuote(provider, current_0, controller.signal, undefined, diagnose, purpose).then(({
        quote,
        review
      }) => {
        if (controller.signal.aborted) return;
        if (review.state === 'expired') throw new Error('quote_expired');
        setSnapshot({
          key,
          revision,
          value: {
            state: 'ready',
            amountUnits: current_0.principalUnits,
            quote,
            review,
            providerFailure: null,
            error: null
          }
        });
        expiry = setTimeout(retry, Math.max(1, Math.min(Date.parse(quote.expiresAt) - Date.now(), 2147483647)));
      }).catch(error => {
        if (controller.signal.aborted) return;
        const failure = providerFailure(error);
        if (failure?.amountBoundary?.kind === 'minimum') setBounds(previous_1 => new Map(previous_1).set(scope, {
          failure,
          expiresAt: Date.now() + 30 * 60 * 1000
        }));
        setSnapshot(previous_2 => ({
          key,
          revision,
          value: {
            ...(previous_2?.key === key ? previous_2.value : empty('error', current_0.principalUnits)),
            state: 'error',
            providerFailure: failure,
            error: failure?.category ?? (error instanceof FundingCurrencyError ? error.message : null) ?? (error instanceof Error && ['quote_expired', 'funding_fx_unavailable', 'onramp_quote_target_mismatch', 'onramp_quote_intent_mismatch', 'onramp_quote_fee_currency_mismatch', 'onramp_card_binding_invalid'].includes(error.message) ? error.message : 'provider_unavailable')
          }
        }));
      });
    }, 800);
    return () => {
      clearTimeout(debounce);
      if (expiry) clearTimeout(expiry);
      controller.abort();
    };
  }, [key, provider, revision, retry, scope, diagnose, purpose]);
  const current_1 = enabled && belowMinimum ? {
    ...empty('idle', intent?.principalUnits ?? null),
    providerFailure: minimum ?? null
  } : key && snapshot?.key === key ? snapshot.revision === revision ? snapshot.value : {
    ...snapshot.value,
    state: 'loading',
    providerFailure: null,
    error: null
  } : empty(key ? 'loading' : 'idle', intent?.principalUnits ?? null);
  return {
    ...current_1,
    minimumNoticeUnits: noticeKey && settledNotice === noticeKey ? minimum?.amountBoundary?.amountUnits ?? null : null,
    retry
  };
}
function empty(state, amountUnits) {
  return {
    state,
    amountUnits,
    quote: null,
    review: null,
    providerFailure: null,
    error: null
  };
}
export function canContinueFundingAmount(units, preview, needsVerification) {
  return Boolean(units && /^[1-9]\d{0,19}$/.test(units) && !preview.providerFailure?.amountBoundary && (needsVerification && (preview.state === 'idle' && !preview.providerFailure || preview.providerFailure?.category === 'connection_required') || preview.amountUnits === units && preview.state === 'ready' && preview.quote && Date.parse(preview.quote.expiresAt) > Date.now() && (preview.review?.state === 'ready' || preview.review?.state === 'challenge_required' || needsVerification && preview.review?.state === 'indicative')));
}
export function useFundingQuoteExpiry(quote, enabled, setReview, refresh) {
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const refreshed = useRef(null);
  useEffect(() => {
    if (!quote || !enabled || refreshed.current === quote) return;
    const delay = Math.max(0, Math.min(Date.parse(quote.expiresAt) - Date.now(), 2147483647));
    const timer = setTimeout(() => {
      refreshed.current = quote;
      setReview(current => current ? {
        ...current,
        state: 'expired'
      } : null);
      refreshRef.current();
    }, delay);
    return () => clearTimeout(timer);
  }, [quote, enabled, setReview]);
}
export function useFundingCheckoutPreview(t0) {
  const $ = _c(61);
  const {
    options,
    method,
    amountUnits,
    discovery,
    state,
    busy,
    refreshing,
    canUpdateAmount,
    providerPaymentMethodId
  } = t0;
  const {
    client,
    provider,
    currency,
    decimals
  } = options;
  let t1;
  if ($[0] !== discovery || $[1] !== method) {
    t1 = Boolean(method && discovery?.connectionRequiredMethods?.includes(method)) || discovery?.status === "connectionRequired" && !((method === "apple_pay" || method === "google_pay") && discovery.guest);
    $[0] = discovery;
    $[1] = method;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const connectionRequired = t1;
  const needsVerification = connectionRequired || Boolean(discovery?.needsContactVerification);
  let t2;
  if ($[3] !== amountUnits || $[4] !== currency || $[5] !== decimals || $[6] !== discovery || $[7] !== method || $[8] !== options.directPurchase || $[9] !== options.fundingFx || $[10] !== providerPaymentMethodId) {
    t2 = method && amountUnits && /^[1-9]\d{0,19}$/.test(amountUnits) && discovery?.context.destination ? {
      destination: discovery.context.destination,
      method,
      ...(providerPaymentMethodId ? {
        providerPaymentMethodId
      } : {}),
      fiatCurrency: currency,
      fiatDecimals: decimals,
      principalUnits: amountUnits,
      ...(!options.directPurchase && options.fundingFx && discovery.context.asset === "USDC_SOL" ? {
        receiveTarget: {
          localUnits: amountUnits,
          fx: options.fundingFx
        }
      } : {}),
      settlement: options.directPurchase ? {
        provider_asset_code: options.directPurchase.binding.providerAssetCode,
        decimals: options.directPurchase.binding.decimals,
        caip19: options.directPurchase.binding.caip19
      } : fundingSettlement(discovery.context.asset)
    } : null;
    $[3] = amountUnits;
    $[4] = currency;
    $[5] = decimals;
    $[6] = discovery;
    $[7] = method;
    $[8] = options.directPurchase;
    $[9] = options.fundingFx;
    $[10] = providerPaymentMethodId;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  const previewIntent = t2;
  let t3;
  if ($[12] !== currency || $[13] !== options.directPurchase || $[14] !== options.fundingFx) {
    t3 = options.directPurchase !== undefined || options.fundingFx === undefined || Boolean(options.fundingFx && fundingRateIsCurrent(options.fundingFx, Date.now()) && options.fundingFx.ratesD9[currency]);
    $[12] = currency;
    $[13] = options.directPurchase;
    $[14] = options.fundingFx;
    $[15] = t3;
  } else {
    t3 = $[15];
  }
  const valuationReady = t3;
  let t4;
  if ($[16] !== client.cacheKey || $[17] !== discovery?.context || $[18] !== discovery?.customerVerification?.customerId || $[19] !== discovery?.guest || $[20] !== discovery?.needsContactVerification || $[21] !== discovery?.status || $[22] !== options.sessionProofId) {
    t4 = JSON.stringify([client.cacheKey, options.sessionProofId, discovery?.status, discovery?.guest, discovery?.needsContactVerification, discovery?.customerVerification?.customerId, discovery?.context.country, discovery?.context.subdivision]);
    $[16] = client.cacheKey;
    $[17] = discovery?.context;
    $[18] = discovery?.customerVerification?.customerId;
    $[19] = discovery?.guest;
    $[20] = discovery?.needsContactVerification;
    $[21] = discovery?.status;
    $[22] = options.sessionProofId;
    $[23] = t4;
  } else {
    t4 = $[23];
  }
  const providerPreview = useFundingQuotePreview(provider, previewIntent, (!options.directPurchase || options.directPurchaseEnabled === true) && state === "amount" && !(options.directPurchase && discovery?.status === "connectionRequired") && valuationReady && !refreshing && options.active !== false, t4, client.observeDiagnostic, needsVerification || method === "card" && !providerPaymentMethodId ? "indicative" : "execution");
  let t5;
  let t6;
  if ($[24] !== client || $[25] !== currency || $[26] !== decimals || $[27] !== options.active || $[28] !== options.fundingFx || $[29] !== options.fundingFxLoading || $[30] !== valuationReady) {
    t5 = () => {
      if (!valuationReady && !options.fundingFxLoading && options.active !== false) {
        client.observeDiagnostic?.(unavailableFundingFx(currency, decimals, options.fundingFx, Date.now()));
      }
    };
    t6 = [client, currency, decimals, valuationReady, options.fundingFx, options.fundingFxLoading, options.active];
    $[24] = client;
    $[25] = currency;
    $[26] = decimals;
    $[27] = options.active;
    $[28] = options.fundingFx;
    $[29] = options.fundingFxLoading;
    $[30] = valuationReady;
    $[31] = t5;
    $[32] = t6;
  } else {
    t5 = $[31];
    t6 = $[32];
  }
  useEffect(t5, t6);
  let t7;
  if ($[33] !== options.fundingFxLoading || $[34] !== providerPreview || $[35] !== valuationReady) {
    t7 = valuationReady ? providerPreview : {
      ...providerPreview,
      state: options.fundingFxLoading ? "loading" : "error",
      error: options.fundingFxLoading ? null : "funding_fx_unavailable",
      quote: null,
      review: null
    };
    $[33] = options.fundingFxLoading;
    $[34] = providerPreview;
    $[35] = valuationReady;
    $[36] = t7;
  } else {
    t7 = $[36];
  }
  const preview = t7;
  useFundingProfileQuote(client, options.sessionProofId, previewIntent, preview.review);
  let t8;
  if ($[37] !== client || $[38] !== method || $[39] !== preview.error || $[40] !== preview.providerFailure?.category) {
    t8 = () => {
      if (preview.error && method && preview.providerFailure?.category !== "connection_required") {
        client.observeFunnel?.({
          stage: method === "card" ? "card_quote" : "amount",
          outcome: "error",
          method,
          errorCode: preview.error
        });
      }
    };
    $[37] = client;
    $[38] = method;
    $[39] = preview.error;
    $[40] = preview.providerFailure?.category;
    $[41] = t8;
  } else {
    t8 = $[41];
  }
  const t9 = preview.providerFailure?.category;
  let t10;
  if ($[42] !== client || $[43] !== method || $[44] !== preview.error || $[45] !== t9) {
    t10 = [client, method, preview.error, t9];
    $[42] = client;
    $[43] = method;
    $[44] = preview.error;
    $[45] = t9;
    $[46] = t10;
  } else {
    t10 = $[46];
  }
  useEffect(t8, t10);
  let t11;
  if ($[47] !== amountUnits || $[48] !== busy || $[49] !== canUpdateAmount || $[50] !== discovery?.context || $[51] !== discovery?.status || $[52] !== method || $[53] !== needsVerification || $[54] !== options.directPurchase || $[55] !== preview || $[56] !== valuationReady) {
    t11 = valuationReady && !busy && canUpdateAmount && Boolean(discovery?.context.destination) && canContinueFundingAmount(amountUnits, preview, needsVerification || preview.providerFailure?.category === "connection_required" || Boolean(options.directPurchase && (method === "card" || discovery?.status === "connectionRequired")));
    $[47] = amountUnits;
    $[48] = busy;
    $[49] = canUpdateAmount;
    $[50] = discovery?.context;
    $[51] = discovery?.status;
    $[52] = method;
    $[53] = needsVerification;
    $[54] = options.directPurchase;
    $[55] = preview;
    $[56] = valuationReady;
    $[57] = t11;
  } else {
    t11 = $[57];
  }
  const canContinueAmount = t11;
  const cardEstimateReady = method === "card" && valuationReady && !busy && canUpdateAmount && preview.state === "ready" && preview.amountUnits === amountUnits && preview.review?.state === "indicative" && Boolean(preview.quote && Date.parse(preview.quote.expiresAt) > Date.now());
  const t12 = canContinueAmount || cardEstimateReady;
  let t13;
  if ($[58] !== preview || $[59] !== t12) {
    t13 = {
      preview,
      canContinueAmount: t12
    };
    $[58] = preview;
    $[59] = t12;
    $[60] = t13;
  } else {
    t13 = $[60];
  }
  return t13;
}